import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { LexGatWordmark } from '../components/Logo';
import { useToast } from '../contexts/ToastContext';
import { useSystem } from '../contexts/SystemContext';
import { isAppHost, isLocalhost, appUrl } from '../lib/domains';
import { PASSWORD_RESET_SENT_MESSAGE } from '../lib/authPassword';
import { resolvePostAuthPath } from '../lib/legalAcceptance';

type AuthView = 'sign-in' | 'sign-up' | 'forgot-password';

export default function Auth() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset, user, userData } =
    useAuth();
  const { config } = useSystem();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<AuthView>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const isSignUp = view === 'sign-up';
  const isForgotPassword = view === 'forgot-password';

  useEffect(() => {
    if (user && userData !== null) {
      const targetPath = resolvePostAuthPath(userData);
      if (isAppHost() || isLocalhost()) {
        navigate(targetPath);
      } else {
        // Defensive: Auth is an app-domain page; if it ever renders on
        // marketing, hop directly to the app domain instead of relying on
        // DomainGuard for an extra round-trip.
        window.location.replace(appUrl(targetPath));
      }
    }
  }, [user, userData, navigate]);

  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
      // Navigation is handled by the useEffect above
    } catch (error: any) {
      showError(error.message || 'Authentication failed');
      setLoading(false);
    }
  };

  const switchView = (next: AuthView) => {
    setView(next);
    setResetEmailSent(false);
    setPassword('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      showError('Enter your email address.');
      return;
    }
    try {
      setLoading(true);
      await sendPasswordReset(email);
      setResetEmailSent(true);
      showSuccess(PASSWORD_RESET_SENT_MESSAGE);
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : 'Could not send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      showError('Please enter both email and password');
      return;
    }
    try {
      setLoading(true);
      if (isSignUp) {
        if (!config.allowNewSignups) {
           showError('New registrations are currently disabled.');
           setLoading(false);
           return;
        }
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (error: any) {
      showError(error.message || 'Authentication failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans bg-[#fafafa] selection:bg-zinc-900 selection:text-white text-zinc-900 overflow-hidden">
      {/* Left side - Branding (Hidden on mobile) */}
      <div className="hidden md:flex flex-col justify-between w-1/2 p-16 bg-zinc-900 text-white relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-zinc-800 rounded-full blur-[120px] opacity-20 -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-zinc-700 rounded-full blur-[100px] opacity-20 translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>

        <div className="relative z-10 cursor-pointer" onClick={() => navigate('/')}>
          <LexGatWordmark className="h-8 w-auto" tone="on-dark" />
        </div>
        
        <div className="relative z-10 max-w-lg mt-auto mb-32">
          <h1 className="text-5xl font-bold tracking-tight leading-[1.1] mb-6">
            Legal practice has been broken for decades.
            <span className="text-zinc-400 block mt-2">We're fixing it.</span>
          </h1>
          <p className="text-lg text-zinc-400 leading-relaxed font-medium">
            One platform. Every case. Total clarity. Step into the future of litigation management in Pakistan.
          </p>
        </div>
        
        <div className="text-sm text-zinc-500 font-medium relative z-10">
          © {new Date().getFullYear()} LexGat Technologies
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 md:p-20 relative overflow-y-auto">
        <div className="w-full max-w-[360px] mx-auto">
          
          {/* Mobile Logo */}
          <div className="flex md:hidden items-center justify-center mb-12" onClick={() => navigate('/')}>
            <LexGatWordmark className="h-8 w-auto" tone="on-light" />
          </div>

          <div className="mb-10 text-center md:text-left">
            <h2 className="text-3xl font-bold tracking-tight mb-2">
              {isForgotPassword
                ? 'Reset your password'
                : isSignUp
                  ? 'Create an account'
                  : 'Welcome back'}
            </h2>
            <p className="text-zinc-500 font-medium">
              {isForgotPassword
                ? 'We will email you a link to choose a new password.'
                : isSignUp
                  ? 'Sign up to create your workspace.'
                  : 'Sign in to access your workspace.'}
            </p>
          </div>

          <div className="mt-8 space-y-6">
            {isForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setResetEmailSent(false);
                    }}
                    placeholder="Enter your email"
                    autoComplete="email"
                    className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-shadow"
                  />
                </div>
                {resetEmailSent && (
                  <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 font-medium leading-relaxed">
                    {PASSWORD_RESET_SENT_MESSAGE}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-zinc-500 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Send reset link'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => switchView('sign-in')}
                  disabled={loading}
                  className="w-full text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  Back to sign in
                </button>
              </form>
            ) : (
              <>
                <form onSubmit={handleEmailAuth} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      autoComplete="email"
                      className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-shadow"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-zinc-700">Password</label>
                      {!isSignUp && (
                        <button
                          type="button"
                          onClick={() => switchView('forgot-password')}
                          disabled={loading}
                          className="text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete={isSignUp ? 'new-password' : 'current-password'}
                      className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-shadow"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-zinc-500 border-t-white rounded-full animate-spin" />
                    ) : isSignUp ? (
                      'Sign Up'
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

                <div className="relative flex items-center py-2">
                  <div className="flex-grow border-t border-zinc-200"></div>
                  <span className="flex-shrink-0 mx-4 text-zinc-400 text-sm font-medium">Or</span>
                  <div className="flex-grow border-t border-zinc-200"></div>
                </div>

                <button
                  onClick={handleGoogleAuth}
                  type="button"
                  disabled={loading}
                  className="w-full py-3 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 shadow-sm hover:shadow"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>

                <div className="text-center mt-6">
                  {config.allowNewSignups ? (
                    <button
                      onClick={() => switchView(isSignUp ? 'sign-in' : 'sign-up')}
                      type="button"
                      className="text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
                      disabled={loading}
                    >
                      {isSignUp
                        ? 'Already have an account? Sign in'
                        : "Don't have an account? Sign up"}
                    </button>
                  ) : (
                    <p className="text-sm text-amber-600 bg-amber-50 py-2 rounded border border-amber-100 font-medium">
                      New registrations are currently closed.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
